// Core
import { ref, onMounted, onUnmounted } from "vue";

// Libraries
import { useIntervalFn } from "@vueuse/core";

// Composables
import { useCustomToast } from "@/composables/useCustomToast";

// Services
import { sentryService } from "@/core/services/sentry/sentryService";

// Utils
import { createLogger } from "@/utils/logger";

// Components
import UpdateToast from "@/components/toast/UpdateToast.vue";

// Types
import type { ToastOptions } from "vue-toastification/dist/types/types";
import { POSITION } from "vue-toastification";

// Create logger instance for PWA updates
const logger = createLogger('PWA Update');

export const usePWAUpdate = () => {
  const updateAvailable = ref(false);
  const registration = ref<ServiceWorkerRegistration | null>(null);
  
  const updateToast = useCustomToast(UpdateToast);

  // Cleanup functions
  const cleanupFunctions: (() => void)[] = [];
  
  // Flag to prevent multiple initializations
  const isInitialized = ref(false);

  const updateSW = async () => {
    try {
      logger.info("Starting update process...");
      
      if (!registration.value) {
        logger.error("No registration available");
        return;
      }
      
      if (!registration.value.waiting) {
        logger.error("No waiting service worker found");
        return;
      }
      
      logger.debug("Found waiting SW:", registration.value.waiting);
      
      // Create a promise to wait for the new SW to become active
      const activated = new Promise<void>((resolve) => {
        const checkState = () => {
          if (registration.value?.active?.state === 'activated') {
            logger.info("New SW is now active");
            resolve();
          }
        };
        
        // Listen for state changes
        if (registration.value?.waiting) {
          registration.value.waiting.addEventListener('statechange', checkState);
        }
        
        // Also listen on controller change
        navigator.serviceWorker.addEventListener('controllerchange', () => {
          logger.info("Controller changed");
          resolve();
        });
        
        // Timeout after 5 seconds
        setTimeout(() => {
          logger.warn("Timeout waiting for SW activation");
          resolve();
        }, 5000);
      });
      
      // Method 1: Try postMessage first
      logger.debug("Sending SKIP_WAITING message...");
      registration.value.waiting.postMessage({ type: 'SKIP_WAITING' });
      
      // Method 2: If the SW has skipWaiting method exposed (some implementations)
      if ('skipWaiting' in registration.value.waiting) {
        logger.debug("Calling skipWaiting directly...");
        (registration.value.waiting as any).skipWaiting();
      }
      
      // Wait for activation or timeout
      await activated;
      
      // Force reload to use the new SW
      logger.info("Reloading page...");
      window.location.reload();
    } catch (error) {
      logger.error("Error updating service worker:", error);
      
      // Determine if this is a critical error that requires reload
      const errorMessage = error instanceof Error ? error.message : String(error);
      const isNetworkError = errorMessage.includes('network') || errorMessage.includes('fetch');
      const isSecurityError = errorMessage.includes('SecurityError') || errorMessage.includes('insecure');
      const isInvalidStateError = errorMessage.includes('InvalidStateError');
      
      // Check if service worker is in a state that might benefit from reload
      const swState = registration.value?.waiting?.state;
      const isSwActivating = swState === 'activating' || swState === 'activated';
      
      sentryService.captureError(error, "pwa-update-failed", {
        title: "PWA Update Failed",
        message: "Failed to update service worker",
        context: {
          hasRegistration: !!registration.value,
          hasWaitingSW: !!registration.value?.waiting,
          waitingState: swState,
          activeState: registration.value?.active?.state,
          errorType: error instanceof Error ? error.name : 'Unknown',
          errorMessage: errorMessage
        }
      });
      
      // Only reload if:
      // 1. Service worker is already activating (might be stuck)
      // 2. It's NOT a network error (reload won't help with network issues)
      // 3. It's NOT a security error (reload won't fix security context)
      if (isSwActivating && !isNetworkError && !isSecurityError) {
        logger.info("SW appears to be activating, attempting reload...");
        window.location.reload();
      } else if (isInvalidStateError) {
        logger.info("Invalid state error, reload might help...");
        window.location.reload();
      } else {
        logger.warn("Update failed but reload may not help. Error:", errorMessage);
        logger.info("User can manually refresh or wait for next update check.");
        
        // Optionally show a user-friendly message
        updateAvailable.value = false; // Reset to prevent toast from showing again
      }
    }
  };

  const showUpdateToast = () => {
    logger.info("Showing update toast...");
    
    const toastOptions: ToastOptions = {
      timeout: false, // Don't auto-dismiss
      closeOnClick: false,
      draggable: false,
      position: POSITION.TOP_CENTER,
      toastId: 'pwa-update-toast' // Prevent duplicate toasts
    };

    updateToast.info(
      {
        title: "Nova versão disponível! 🚀",
        message: "Uma nova versão do aplicativo está disponível com melhorias e correções. Atualize para obter a melhor experiência.",
        updateAction: updateSW,
        laterAction: () => {
          // Just close the toast, the update will be available next time
          logger.debug("Update postponed by user");
          updateAvailable.value = false; // Reset flag to prevent showing again in this session
        }
      },
      toastOptions
    );
  };

  const initPWAUpdate = async () => {
    // Prevent multiple initializations
    if (isInitialized.value) {
      logger.warn("Already initialized, skipping duplicate initialization");
      return;
    }

    // Check if service workers are supported
    if (!('serviceWorker' in navigator)) {
      logger.info("Service Workers not supported");
      return;
    }

    logger.info("Initializing PWA update detection...");

    try {
      isInitialized.value = true;
      
      // Wait for service worker to be ready first
      await navigator.serviceWorker.ready;
      
      // Check if there's already a registration
      const existingRegistration = await navigator.serviceWorker.getRegistration();
      
      if (existingRegistration) {
        registration.value = existingRegistration;
        
        logger.debug("Service Worker registration found", {
          scope: existingRegistration.scope,
          hasActive: !!existingRegistration.active,
          activeState: existingRegistration.active?.state,
          hasInstalling: !!existingRegistration.installing,
          hasWaiting: !!existingRegistration.waiting
        });
        
        // Check if there's an update waiting
        if (existingRegistration.waiting) {
          logger.info("Found waiting SW on init");
          updateAvailable.value = true;
          showUpdateToast();
          return;
        }
        
        logger.debug("No waiting SW found, setting up listeners...")

        // Listen for updates
        const updateFoundHandler = () => {
          const newWorker = existingRegistration.installing;
          
          if (newWorker) {
            const stateChangeHandler = () => {
              logger.debug("Worker state changed:", newWorker.state);
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                logger.info("New SW installed, showing toast");
                updateAvailable.value = true;
                showUpdateToast();
              }
            };
            newWorker.addEventListener('statechange', stateChangeHandler);
            
            // Add cleanup for statechange listener
            cleanupFunctions.push(() => {
              newWorker.removeEventListener('statechange', stateChangeHandler);
            });
          }
        };
        
        existingRegistration.addEventListener('updatefound', updateFoundHandler);
        cleanupFunctions.push(() => {
          existingRegistration.removeEventListener('updatefound', updateFoundHandler);
        });
      }

      // Listen for messages from the service worker
      const messageHandler = (event: MessageEvent) => {
        logger.debug("Received SW message:", event.data);
        if (event.data && event.data.type === 'SW_UPDATED') {
          logger.info("SW_UPDATED message received");
          updateAvailable.value = true;
          showUpdateToast();
        }
      };
      
      navigator.serviceWorker.addEventListener('message', messageHandler);
      cleanupFunctions.push(() => {
        navigator.serviceWorker.removeEventListener('message', messageHandler);
      });

      // Check for updates periodically (every 60 seconds when page is active)
      const checkForUpdates = async () => {
        try {
          if (document.visibilityState === 'visible' && registration.value) {
            // Check if registration is still valid
            const currentReg = await navigator.serviceWorker.getRegistration();
            if (!currentReg) {
              logger.warn("Service worker registration lost, re-initializing...");
              registration.value = null;
              isInitialized.value = false;
              await initPWAUpdate();
              return;
            }
            
            // Update our reference if needed
            if (currentReg !== registration.value) {
              logger.debug("Registration reference updated");
              registration.value = currentReg;
            }
            
            // Check the state before updating
            const isValidState = currentReg.active && 
                               currentReg.active.state === 'activated' &&
                               !currentReg.installing &&
                               !currentReg.waiting;
            
            if (isValidState) {
              logger.debug("Checking for updates...");
              await currentReg.update();
            } else {
              logger.debug("Skipping update check - SW not in valid state", {
                hasActive: !!currentReg.active,
                activeState: currentReg.active?.state,
                hasInstalling: !!currentReg.installing,
                hasWaiting: !!currentReg.waiting
              });
            }
          }
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : String(error);
          
          // Handle specific error types
          if (errorMessage.includes('InvalidStateError')) {
            logger.warn("Service worker in invalid state, will retry later");
            // Don't log to Sentry for expected invalid state errors
            return;
          }
          
          logger.error("Error checking for updates:", error);
          sentryService.captureError(error, "pwa-update-check-failed", {
            title: "PWA Update Check Failed",
            message: "Failed to check for service worker updates",
            context: {
              errorType: error instanceof Error ? error.name : 'Unknown',
              errorMessage: errorMessage
            }
          });
        }
      };

      // Check for updates when page becomes visible
      const visibilityChangeHandler = () => {
        if (document.visibilityState === 'visible') {
          logger.debug("Page became visible, checking for updates...");
          checkForUpdates();
        }
      };
      
      document.addEventListener('visibilitychange', visibilityChangeHandler);
      cleanupFunctions.push(() => {
        document.removeEventListener('visibilitychange', visibilityChangeHandler);
      });
      
      // Initial check
      const initialCheckTimeout = setTimeout(checkForUpdates, 5000);
      cleanupFunctions.push(() => {
        clearTimeout(initialCheckTimeout);
      });
      
      // Periodic checks every 60 seconds when page is active
      const { pause: pauseInterval } = useIntervalFn(() => {
        if (document.visibilityState === 'visible' && registration.value) {
          checkForUpdates();
        }
      }, 60000);
      
      cleanupFunctions.push(() => {
        pauseInterval();
      });
      
    } catch (error) {
      logger.error("Error initializing PWA update:", error);
      sentryService.captureError(error, "pwa-init-failed", {
        title: "PWA Initialization Failed",
        message: "Failed to initialize PWA update detection"
      });
      
      // Reset initialization flag on error to allow retry
      isInitialized.value = false;
    }
  };

  // Manual cleanup function for app-level usage
  const cleanup = () => {
    cleanupFunctions.forEach(cleanup => cleanup());
    cleanupFunctions.length = 0; // Clear the array
    isInitialized.value = false; // Allow re-initialization if needed
  };

  onMounted(() => {
    // Initialize PWA update detection after component mounts
    setTimeout(initPWAUpdate, 1000);
  });

  onUnmounted(() => {
    cleanup();
  });

  return {
    updateAvailable,
    updateSW,
    showUpdateToast,
    initPWAUpdate,
    cleanup
  };
};